import { createAction, prepareAutoBatched } from '@reduxjs/toolkit';
import { UserId } from 'features/users/types';
import { ActiveMediaDevicePayload } from 'features/user-media/types';
import type { SignalingEventPayload } from 'services/signaling';
import {
  ActiveSpeakerChanged,
  BulkMuteRemoteMediaPayload,
  type DeviceAskedToUnmuteRemotely,
  DeviceMutedRemotely,
  ExternalStreamingStarted,
  ExternalStreamingStopped,
  RemoteTrackReceived,
  RTCEventPayload,
  SpeakerStoppedTalking,
  StreamingErrorMessage,
  StreamingStarted,
  StreamingStopped,
} from './types';

export type VideoroomErrorSource = 'receiver' | 'streaming-receiver' | 'publisher';
export interface VideoroomErrorPayload extends RTCEventPayload<{
  error_code: number;
  error: string;
}> {
  source: VideoroomErrorSource;
}
export const videoroomError = createAction<VideoroomErrorPayload>('streaming/videoroomError');
export const streamingError = createAction<StreamingErrorMessage>('streaming/streamingError');

// streaming lifecycle events
export const streamStarted = createAction<StreamingStarted>('signaling/streamStarted');
export const streamStopped = createAction<StreamingStopped>('signaling/streamStopped');

export const externalStreamStarted = createAction<ExternalStreamingStarted>(
  'signaling/externalStreamStarted'
);
export const externalStreamStopped = createAction<ExternalStreamingStopped>(
  'signaling/externalStreamStopped'
);

export const publisherJoined = createAction<RTCEventPayload>('streaming/publisherJoined');
export const publisherLeft = createAction<RTCEventPayload>('streaming/publisherLeft');

export const subscriberAttached = createAction<RTCEventPayload>('streaming/subscriberAttached');

export const signalingAspectRatioAdjusted = createAction<{ id: UserId; keep: boolean }>(
  'signaling/aspectRatioAdjusted'
);

export const activeSpeakerChanged = createAction(
  'signaling/activeSpeakerChanged',
  prepareAutoBatched<ActiveSpeakerChanged>()
);
export const speakerStoppedTalking = createAction(
  'signaling/speakerStoppedTalking',
  prepareAutoBatched<SpeakerStoppedTalking>()
);
export const streamingDeviceChanged = createAction<ActiveMediaDevicePayload>(
  'streaming/streamingDeviceChanged'
);

// init events
export const streamingConnectionEstablished = createAction('streaming/connectionEstablished');

export const publishingConnectionEstablished = createAction(
  'streaming/publishingConnectionEstablished'
);

// local interactions
export const localVideoToggled = createAction('streaming/localVideoToggled');
export const localAudioToggled = createAction('streaming/localAudioToggled');

// remote audio mute
export const muteRemoteAudio = createAction<UserId>('streaming/muteRemoteAudio');
export const audioMutedRemotely = createAction<DeviceMutedRemotely>('signaling/audioMutedRemotely');

// remove audio unmute
export const askUnmuteRemoteAudio = createAction<UserId>('streaming/askUnmuteRemoteAudio');
export const remoteAudioUnmuteAccepted = createAction('streaming/remoteAudioUnmuteAccepted');
export const audioUnmuteAsked = createAction<SignalingEventPayload<DeviceAskedToUnmuteRemotely>>(
  'signaling/audioUnmuteAsked'
);
export const bulkMuteRemoteAudio = createAction<BulkMuteRemoteMediaPayload | undefined>(
  'streaming/bulkMuteRemoteAudio'
);

// remote video mute
export const muteRemoteVideo = createAction<UserId>('streaming/muteRemoteVideo');
export const videoMutedRemotely = createAction<DeviceMutedRemotely>('signaling/videoMutedRemotely');
export const bulkMuteRemoteVideo = createAction<BulkMuteRemoteMediaPayload | undefined>(
  'streaming/bulkMuteRemoteVideo'
);

// remove video unmute
export const askUnmuteRemoteVideo = createAction<UserId>('streaming/askUnmuteRemoteVideo');
export const remoteVideoUnmuteAccepted = createAction('streaming/remoteVideoUnmuteAccepted');
export const videoUnmuteAsked = createAction<DeviceMutedRemotely>('signaling/videoUnmuteAsked');

export const remoteTrackReceived = createAction<RemoteTrackReceived>(
  'streaming/remoteTrackReceived'
);

export const receivingPluginCleanup = createAction<{ pluginHandle: string; instanceId: string }>(
  'streaming/receavingPluginCleanup'
);

export const pluginHandleAttachFailed = createAction<string>('streaming/pluginHandleAttachFailed');

export const publisherJoinedWithoutBroadcast = createAction(
  'streaming/publisherJoinedWithoutBroadcast'
);
